/* eslint-disable @typescript-eslint/no-explicit-any */
import { format } from 'date-fns'
import React from 'react'
import NewsCard from '~/components/Cards/NewsEvents/NewsCard/NewsCard'
import ProfileCard from '~/components/Cards/ProfileCard/ProfileCard'
import MarkDown from '~/components/Helpers/MarkDown'
import Separator from '~/components/UI/General/Separator/Separator'
import Tag from '~/components/UI/General/Tag/Tag'
import { isDevelopment } from '~/utils/misc'
import AccommodationCard from './ResItems/AccommodationCard'

interface ResultsListProps {
  items: any[]
  contentType?: string
}

const ResultsList: React.FC<ResultsListProps> = ({ items, contentType }) => {
  // You can switch on contentType to render different layouts
  // console.log(items)
  return (
    <>
      {items.map((item, index) => {
        switch (contentType) {
          case 'accommodationRoomTypes':
            return (
              <AccommodationCard
                key={item.sys.id + '-' + index}
                item={item}
                index={index}
              />
            )
          case 'studyUndergraduateCoursePage':
          case 'pageCoursePGT':
          case 'pageCoursePGR':
          case 'pageCourseCPD':
            return (
              <a
                key={item.sys.id + '-' + index}
                href={item.sys.uri}
                className='group unstyled-link gap-l flex items-center'
              >
                <div className='py-2xs space-y-2xs-plus flex h-auto grow flex-col'>
                  <div className='space-y-xs'>
                    <h3 className='font-bold group-hover:underline'>
                      {item?.courseVariants &&
                        item?.courseVariants[0]?.qualification?.entryTitle}{' '}
                      {item.heading || item.entryTitle}
                    </h3>
                    <p className='tag'>{item?.studyLevel?.entryTitle}</p>
                  </div>
                  <div className='space-y-m mt-2xs'>
                    <MarkDown content={item.description} />
                  </div>
                  <div className='gap-xs mt-xs-plus mb-m flex flex-wrap'>
                    {item?.studyMode && item?.studyMode.length > 0 && (
                      <Tag
                        data={{
                          label: item?.studyMode[0]?.entryTitle,
                          taxonomy: undefined,
                          backgroundColour: 'light-aqua',
                          icon: {
                            asset: {
                              sys: {
                                uri: '/site-elements/icons/single-colour/ink-blue/clock.svg'
                              }
                            }
                          },
                          link: undefined
                        }}
                      />
                    )}
                    {item?.courseVariants.length > 0 && (
                      <Tag
                        data={{
                          label: item?.courseVariants[0]?.duration?.entryTitle,
                          taxonomy: undefined,
                          backgroundColour: 'light-pink',
                          icon: {
                            asset: {
                              sys: {
                                uri: '/site-elements/icons/single-colour/ink-blue/calendar.svg'
                              }
                            }
                          },
                          link: undefined
                        }}
                      />
                    )}
                    {item.entryRequirements &&
                      item.entryRequirements.length > 0 &&
                      item.entryRequirements[0]?.entryGrades && (
                        <Tag
                          data={{
                            label:
                              item.entryRequirements[0]?.entryGrades
                                ?.entryTitle + ' points',
                            taxonomy: undefined,
                            backgroundColour: 'light-blue',
                            icon: {
                              asset: {
                                sys: {
                                  uri: '/site-elements/icons/single-colour/ink-blue/checkbox-checked.svg'
                                }
                              }
                            },
                            link: undefined
                          }}
                        />
                      )}
                  </div>
                  <Separator />
                </div>
              </a>
            )
          case 'pageScholarship':
            return (
              <a
                key={item.sys.id + '-' + index}
                href={item.sys.uri}
                className='group unstyled-link gap-l flex items-center'
              >
                <div className='py-2xs space-y-2xs-plus flex h-auto grow flex-col'>
                  <div className='space-y-xs'>
                    <h3 className='font-bold group-hover:underline'>
                      {item.title || item.entryTitle}
                    </h3>
                    <p className='tag'>{item.awardedBy?.entryTitle}</p>
                  </div>
                  <div className='space-y-m mt-2xs'>
                    <MarkDown content={item.description} />
                  </div>
                  <div className='gap-xs mt-xs-plus mb-m flex flex-wrap'>
                    {item?.benefit && (
                      <Tag
                        data={{
                          label:
                            item?.benefit?.entryTitle.split(/[[\s–][\s-]+/)[0],
                          taxonomy: undefined,
                          backgroundColour: 'light-orange',
                          icon: {
                            asset: {
                              sys: {
                                uri: '/site-elements/icons/single-colour/ink-blue/wallet.svg'
                              }
                            }
                          },
                          link: undefined
                        }}
                      />
                    )}
                    {item?.type && (
                      <Tag
                        data={{
                          label: item.type.entryTitle.split(/[[\s–][\s-]+/)[0],
                          taxonomy: undefined,
                          backgroundColour: 'light-pink',
                          icon: {
                            asset: {
                              sys: {
                                uri: '/site-elements/icons/single-colour/ink-blue/bullet-list-69.svg'
                              }
                            }
                          },
                          link: undefined
                        }}
                      />
                    )}
                    {item.appStatus?.entryDescription ===
                      'automatically-applied' && (
                      <Tag
                        data={{
                          label: 'Automatically applied',
                          taxonomy: undefined,
                          backgroundColour: 'light-aqua',
                          icon: {
                            asset: {
                              sys: {
                                uri: '/site-elements/icons/single-colour/ink-blue/checkbox-checked.svg'
                              }
                            }
                          },
                          link: undefined
                        }}
                      />
                    )}
                  </div>
                  <Separator />
                </div>
              </a>
            )
          case 'pageNewsArticle':
            return (
              <a
                href={item.sys.uri}
                key={item.sys.id + '-' + index}
                className='unstyled-link sm:gap-m gap-s group border-blue-d-40 py-m flex max-w-[80ch] border-b'
              >
                {item.image && (
                  <div className='max-h-xl sm:max-h-2xl aspect-square max-w-xl shrink-0 sm:max-w-2xl lg:max-h-[96px] lg:max-w-[96px]'>
                    <img
                      className='h-full w-full object-cover'
                      src={`${
                        isDevelopment &&
                        !item.image?.asset.sys.uri.startsWith('https')
                          ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                          : ''
                      }${item.image?.asset.sys.uri}`}
                      alt={item.image?.altText}
                    />
                  </div>
                )}
                <div className='sm:gap-xs gap-2xs-plus flex flex-col'>
                  <div className='tag'>{format(item.date, 'dd MMM yyyy')}</div>
                  <div className='lg:text-h4-lg sm:text-h4-md text-base-lg line-clamp-2 font-bold group-hover:underline'>
                    {item.entryTitle}
                  </div>
                  <p className='lg:text-base-lg sm:text-base-md text-base-sm line-clamp-2'>
                    {item.description}
                  </p>
                </div>
              </a>
            )
          case 'pageEvent':
          case 'pageEventSC':
            return (
              <a
                href={item.sys.uri}
                key={item.sys.id + '-' + index}
                className='unstyled-link sm:gap-m gap-s group border-blue-d-40 pb-m not-last:mb-m flex max-w-[80ch] border-b'
              >
                {item.image && (
                  <div className='max-h-xl sm:max-h-2xl aspect-square max-w-xl shrink-0 sm:max-w-2xl lg:max-h-[96px] lg:max-w-[96px]'>
                    <img
                      className='h-full w-full object-cover'
                      src={`${
                        isDevelopment &&
                        !item.image?.asset.sys.uri.startsWith('https')
                          ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                          : ''
                      }${item.image?.asset.sys.uri}`}
                      alt={item.image?.altText}
                    />
                  </div>
                )}
                <div className='sm:gap-xs gap-2xs-plus flex flex-col'>
                  <div className='lg:text-h4-lg sm:text-h4-md text-base-lg line-clamp-2 font-bold group-hover:underline'>
                    {item.entryTitle}
                  </div>
                  {item?.date && (
                    <div className='tag'>
                      {format(item.date?.from, 'dd MMM yyyy')}
                    </div>
                  )}
                  <p className='lg:text-base-lg sm:text-base-md text-base-sm line-clamp-2'>
                    {item.description}
                  </p>
                </div>
              </a>
            )
          case 'pageCaseStudyResearch':
            return (
              <NewsCard
                key={item.sys.id + '-' + index}
                className='mb-0 max-h-[365px] min-w-[calc(33.33%-16px)] sm:max-w-1/2 lg:max-w-1/3'
                carousel={true}
                content={{
                  heading: item.seoStandard.title || item.entryTitle,
                  description: item.description,
                  categories: [
                    {
                      data: {
                        taxonomy: item.status,
                        label: item.status.entryTitle,
                        backgroundColour:
                          item.status.entryTitle === 'Complete'
                            ? 'light-aqua'
                            : 'light-orange',
                        showIcon: false
                      }
                    }
                  ],
                  sys: {
                    contentTypeId: 'pageCaseStudyResearch',
                    uri: item.sys.uri
                  },
                  featured: false,
                  entryTitle: item.seoStandard.title,
                  image: item.heroStandardAvantGarde[0]?.image,
                  backgroundColour: 'light-grey',
                  showDescription: true
                }}
              />
            )
          case 'staffProfile':
            return (
              <ProfileCard
                key={item.sys.id + '-' + index}
                className='mb-0 max-h-[365px] min-w-[calc(33.33%-16px)] sm:max-w-1/2 lg:max-w-1/3'
                carousel={true}
                content={{ ...item, backgroundColour: 'ink-blue' }}
              />
            )
          default:
            return (
              <a
                key={item.sys.id + '-' + index}
                href={item.sys.uri}
                className='group unstyled-link gap-l flex items-center'
              >
                <div className='py-2xs space-y-2xs-plus flex h-auto grow flex-col'>
                  <div className='space-y-xs'>
                    <h3 className='font-bold group-hover:underline'>
                      {item?.courseVariants &&
                        item?.courseVariants[0]?.qualification?.entryTitle}{' '}
                      {item.heading || item.entryTitle}
                    </h3>
                  </div>
                  <div className='space-y-m mt-2xs'>
                    <MarkDown content={item.description} />
                  </div>

                  <Separator />
                </div>
              </a>
            )
        }
      })}
    </>
  )
}

export default ResultsList
